<script lang="ts" setup>
import mitt from 'mitt'
import type { MLoadRef } from './MLoad.vue'

const props = defineProps<{
  /** 评论区ID */
  areaId: number
}>()

const order = ref<'desc' | 'asc'>('desc')
const page = ref(1)
const empty = ref(false)
const loaded = ref(false)
const commentList = ref<CommentDetail[]>([])
const loadRef = ref<MLoadRef>()

const { data: userInfo } = useAuth()

const { data: commentAreaDetail, refresh: refreshDetail } = await useAsyncData(
  `commentAreaDetail:${props.areaId}`,
  () => fetchCommentAreaDetail(props.areaId)
)

const pinComment = ref<CommentDetail | null>()
if (commentAreaDetail.value && commentAreaDetail.value?.pin_floor_id !== 0) {
  const { data } = await useAsyncData(`pinComment:${props.areaId}`, () =>
    fetchCommentById(commentAreaDetail.value!.pin_floor_id)
  )
  pinComment.value = data.value
}

onMounted(() => {
  getCommentList()
})

const $toast = useToast()
const $confirm = useConfirm()
const richTextRef = ref<RichTextRefIface>()
const emitter = mitt<RichTextEvent>()
const newComment = ref('')
const newImage = ref<string[]>([])

const length = computed(() => richTextRef.value?.getContentLength() || 0)

watch(order, () => {
  getCommentList()
})

async function getCommentList() {
  page.value = 1
  const result = await fetchCommentList(props.areaId, page.value, order.value)
  commentList.value = Array.isArray(result) ? result : []
  empty.value = commentList.value.length === 0
  loaded.value = true
  loadRef.value?.check()
}

async function handleUpload(image?: { url?: string; file?: File }) {
  if (image?.url) {
    newImage.value = [image.url]
  } else if (image?.file) {
    const res = await requestUploadImage(image.file)
    if (res.code === 1) {
      newImage.value = [res.data.file.file_path]
    } else {
      $toast.error(res.msg)
    }
  }
}

async function sendMessage() {
  const { data: userInfo } = useAuth()
  if (!userInfo.value.login) {
    useEmitter().emit('requestLogin')
    return
  }
  if (length.value === 0 && newImage.value.length === 0) {
    $toast.error('亲，至少输入点东西再发送吧')
    return
  }
  if (length.value > 200) {
    $toast.error('评论内容不能超过200字符哦喵~')
    return
  }
  const res = await requestCreateComment(
    props.areaId,
    newComment.value,
    newImage.value
  )
  if (res.code === 1) {
    newComment.value = ''
    emitter.emit('clear')
    newImage.value.length = 0
    refreshDetail()
    getCommentList()
  } else {
    $toast.error(res.msg || '发送失败')
  }
}

function removeImg(e: Event) {
  e.preventDefault()
  e.stopPropagation()
  newImage.value.length = 0
}

async function loadMore(ev?: { complete: () => void }) {
  if (empty.value || !loaded.value) {
    ev?.complete()
    return
  }
  page.value++
  const more = await fetchCommentList(props.areaId, page.value, order.value)
  empty.value = more.length === 0
  commentList.value.push(...more)
  ev?.complete()
}
</script>

<template>
  <div class="m-comment">
    <div class="m-comment-input">
      <div class="m-comment-input__header">发布评论</div>
      <RichText
        ref="richTextRef"
        v-model="newComment"
        :emitter="emitter"
        :max-length="200"
        mention
        class="editor"
        toolbar="#create-comment-toolbar"
        placeholder="请输入友善的评论内容哦喵~"
        :formats="['sticker', 'mention']"
      />
      <RichTextToolBar
        id="create-comment-toolbar"
        :emitter="emitter"
        sticker
        mention
        custom-image
        @on-custom-image-upload="handleUpload"
      >
        <n-button
          :disabled="length === 0 && newImage.length === 0"
          type="primary"
          size="small"
          @click="sendMessage"
        >
          发送
        </n-button>
      </RichTextToolBar>
      <div v-if="newImage.length > 0" class="m-comment-input__image">
        <m-image-preview>
          <m-img :key="newImage[0]" :src="newImage[0]" class="preview" />
        </m-image-preview>
        <m-icon icon="lucide-x" class="remove" @click="removeImg" />
      </div>
    </div>
    <div class="m-comment-list">
      <div class="m-comment-list__header">
        <div>全部评论({{ commentAreaDetail?.floor_count }})</div>
        <div class="tabs">
          <n-tabs v-model:value="order" type="segment">
            <n-tab-pane name="desc" tab="最新" />
            <n-tab-pane name="asc" tab="最早" />
          </n-tabs>
        </div>
      </div>
      <div class="m-comment-list__main">
        <MCommentCard
          v-if="pinComment"
          is-pin
          :comment-id="pinComment.id"
          :floor="pinComment.floor_num"
          :html="pinComment.content"
          :user="pinComment.user_info"
          :image="pinComment.content_ext?.images[0]"
          :is-author="commentAreaDetail?.user_id === pinComment.user_id"
          :is-self="userInfo?.user.id === pinComment.user_id"
          :is-delete="pinComment.is_delete"
          :reply-count="+pinComment.reply_count"
          :second-reply="pinComment.second_reply"
          :created-at="pinComment.created_at"
          :like-status="pinComment.like_status"
        />
        <MCommentCard
          v-for="c in commentList?.filter((c) => c.id !== pinComment?.id)"
          :key="c.id"
          :comment-id="c.id"
          :floor="c.floor_num"
          :html="c.content"
          :image="c.content_ext?.images[0]"
          :user="c.user_info"
          :is-author="commentAreaDetail?.user_id === c.user_id"
          :is-self="userInfo?.user.id === c.user_id"
          :is-delete="c.is_delete"
          :reply-count="+c.reply_count"
          :second-reply="c.second_reply"
          :created-at="c.created_at"
          :like-status="c.like_status"
        />
        <MLoad ref="loadRef" :empty="empty" @on-scroll-end="loadMore" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.m-comment {
  margin-bottom: 12px;
  .m-comment-input {
    padding: 8px 32px;
    background-color: #fff;
    border-radius: 8px;
    margin-top: 12px;
    &__header {
      font-size: 18px;
      color: var(--color-gray-900);
      font-weight: bold;
      width: 100%;
      line-height: 50px;
    }
    :deep(.rich-text-toolbar) {
      padding: 8px 0;
    }
    .m-icon {
      font-size: 24px;
      color: var(--color-gray-400);
    }
    .preview {
      width: 256px;
      max-height: 256px;
      border-radius: 6px;
    }
    &__image {
      position: relative;
      width: fit-content;
    }
    .remove {
      cursor: pointer;
      position: absolute;
      top: 8px;
      right: 8px;
      background-color: rgba($color: #000000, $alpha: 0.5);
      border-radius: 50%;
      color: #fff;
      padding: 4px;
    }
  }
  .m-comment-list {
    padding: 8px 32px;
    background-color: #fff;
    border-radius: 4px;
    margin-top: 12px;
    &__header {
      font-size: 18px;
      color: var(--color-gray-900);
      font-weight: bold;
      width: 100%;
      line-height: 50px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .tabs {
        width: 120px;
      }
    }
    &__main {
      > * {
        border-bottom: 1px solid var(--color-gray-100);
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .m-comment-input,
  .m-comment-list {
    padding: 8px 0 !important;
  }
}
</style>
